import { useState } from "react";
import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "Privacy Policy | BangunRumahKita" },
    {
      name: "description",
      content: "Privacy policy for BangunRumahKita by PT Arsinesia Karya",
    },
  ];
};

export default function PrivacyPolicy() {
  const [isEnglish, setIsEnglish] = useState(true);

  const toggleLanguage = () => setIsEnglish((prev) => !prev);

  return (
    <main className="w-global mx-auto pt-[5rem] lg:pt-[7rem] pb-[5rem] lg:pb-[18rem] text-gray-800">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Privacy Policy</h1>
        <button
          onClick={toggleLanguage}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          {isEnglish ? "Bahasa Indonesia" : "English"}
        </button>
      </div>

      <p className="mb-4">
        {isEnglish ? "Last updated" : "Terakhir diperbarui"}:{" "}
        {new Date().toLocaleDateString("id-ID")}
      </p>

      <div className="mb-8">
        <img
          src="https://ik.imagekit.io/didigi/jasa_renovasi_rumah_di_bekasi/jasa-renovasi-di-bekasi_arsinesia_logo-square.webp" // Ganti dengan path/logo sebenarnya
          alt="BangunRumahKita Logo"
          className="w-32 h-auto mb-2"
        />
        <p className="text-sm text-gray-600">
          {isEnglish ? (
            <>
              This privacy policy applies to the app{" "}
              <strong>arsinesia-app</strong>, owned and operated by{" "}
              <strong>PT BAROKAH NUR THOIBAH</strong>. Visit our official
              website at{" "}
              <a
                href="https://arsinesia.com"
                className="text-blue-600 underline"
                target="_blank"
                rel="noreferrer"
              >
                www.arsinesia.com
              </a>
              .
            </>
          ) : (
            <>
              Kebijakan privasi ini berlaku untuk aplikasi{" "}
              <strong>BangunRumahKita</strong>, yang dimiliki dan dioperasikan
              oleh <strong>PT Arsinesia Karya</strong>. Kunjungi situs resmi
              kami di{" "}
              <a
                href="https://arsinesia.com"
                className="text-blue-600 underline"
                target="_blank"
                rel="noreferrer"
              >
                www.arsinesia.com
              </a>
              .
            </>
          )}
        </p>
      </div>

      {isEnglish ? (
        <>
          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              1. Information We Collect
            </h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>IP address and location data</li>
              <li>Usage data when you access our site</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              2. How We Use Your Information
            </h2>
            <p>
              We use the information to provide services, contact you, send
              updates, and improve site performance and security.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              3. Data Storage and Protection
            </h2>
            <p>
              Personal data is stored securely and only as long as necessary. We
              use technical and organizational methods to protect your data from
              unauthorized access or misuse.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              4. Information Sharing
            </h2>
            <p>
              We do not share your personal information with third parties
              without your consent, except as required by law.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">5. Cookies</h2>
            <p>
              This site uses cookies to improve user experience. You can set
              your browser to refuse cookies.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">6. Contact</h2>
            <p>
              For questions about this Privacy Policy, contact us at:{" "}
              <a
                href="mailto:info@arsinesia.com"
                className="text-blue-600 underline"
              >
                info@arsinesia.com
              </a>
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              7. Use of Meta (Facebook & Instagram) Data
            </h2>
            <p>
              This app integrates content from Meta platforms (Facebook and
              Instagram) using official APIs and oEmbed permissions. We only
              access and display public content (posts, images, or videos) from
              our official pages. We do not store, share, or modify any user
              data from Meta. All usage complies with{" "}
              <a
                href="https://developers.facebook.com/policy/"
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Meta Platform Policies
              </a>
              .
            </p>
          </section>
        </>
      ) : (
        <>
          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              1. Informasi yang Kami Kumpulkan
            </h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Nama lengkap</li>
              <li>Alamat email</li>
              <li>Nomor telepon</li>
              <li>Alamat IP dan data lokasi</li>
              <li>Data penggunaan saat mengakses situs kami</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              2. Cara Kami Menggunakan Informasi Anda
            </h2>
            <p>
              Kami menggunakan informasi tersebut untuk menyediakan layanan,
              menghubungi Anda, mengirim pembaruan, serta meningkatkan performa
              dan keamanan situs.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              3. Penyimpanan dan Perlindungan Data
            </h2>
            <p>
              Data pribadi disimpan dengan aman dan hanya selama diperlukan.
              Kami menggunakan metode teknis dan organisasi untuk melindungi
              data Anda dari akses tidak sah atau penyalahgunaan.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              4. Pembagian Informasi
            </h2>
            <p>
              Kami tidak membagikan informasi pribadi Anda kepada pihak ketiga
              tanpa persetujuan Anda, kecuali diwajibkan oleh hukum.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">5. Cookie</h2>
            <p>
              Situs ini menggunakan cookie untuk meningkatkan pengalaman
              pengguna. Anda dapat mengatur browser Anda untuk menolak cookie.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">6. Kontak</h2>
            <p>
              Jika Anda memiliki pertanyaan tentang Kebijakan Privasi ini,
              hubungi kami di:{" "}
              <a
                href="mailto:info@arsinesia.com"
                className="text-blue-600 underline"
              >
                info@arsinesia.com
              </a>
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              7. Penggunaan Data dari Meta (Facebook & Instagram)
            </h2>
            <p>
              Aplikasi ini mengintegrasikan konten dari platform Meta (Facebook
              dan Instagram) melalui API resmi dan izin oEmbed. Kami hanya
              mengakses dan menampilkan konten publik dari halaman resmi kami.
              Kami tidak menyimpan, membagikan, atau memodifikasi data pengguna
              dari Meta. Seluruh penggunaan mengikuti{" "}
              <a
                href="https://developers.facebook.com/policy/"
                className="text-blue-600 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Kebijakan Platform Meta
              </a>
              .
            </p>
          </section>
        </>
      )}
    </main>
  );
}
